import { Fragment, type ReactNode } from "react";

/**
 * Small, dependency-free Markdown renderer.
 * Renders to React elements (never raw HTML), so content is XSS-safe by
 * construction. Supports headings, paragraphs, lists, quotes, rules, images,
 * fenced code, links, bold, italic, inline code and GitHub Flavored Markdown
 * extras: tables (with alignment), strikethrough and bare-URL autolinks.
 * Raw HTML is never rendered; stray empty anchors (<a id="x"></a>) are dropped.
 */

const TABLE_SEP = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;

function splitRow(line: string): string[] {
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|") && !t.endsWith("\\|")) t = t.slice(0, -1);
  const cells: string[] = [];
  let cur = "";
  for (let k = 0; k < t.length; k++) {
    const ch = t[k];
    if (ch === "\\" && t[k + 1] === "|") {
      cur += "|";
      k++;
    } else if (ch === "|") {
      cells.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

function isTableStart(lines: string[], n: number): boolean {
  const a = lines[n] ?? "";
  const b = lines[n + 1] ?? "";
  return a.includes("|") && TABLE_SEP.test(b) && b.includes("-") && (a.trim().startsWith("|") || b.includes("|"));
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern =
    /(!\[[^\]]*\]\([^)\s]+\))|(\[[^\]]+\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(~~[^~]+~~)|(\*[^*]+\*)|(`[^`]+`)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${i++}`;

    if (token.startsWith("![")) {
      const alt = token.slice(2, token.indexOf("]"));
      const src = token.slice(token.indexOf("](") + 2, -1);
      nodes.push(
        <img key={key} src={src} alt={alt} loading="lazy" className="my-6 w-full rounded-xl" />,
      );
    } else if (token.startsWith("[")) {
      const label = token.slice(1, token.indexOf("]"));
      const href = token.slice(token.indexOf("](") + 2, -1);
      const external = /^https?:\/\//i.test(href);
      nodes.push(
        <a
          key={key}
          href={href}
          className="text-primary underline underline-offset-4"
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {label}
        </a>,
      );
    } else if (token.startsWith("**")) {
      nodes.push(
        <strong key={key} className="font-semibold text-foreground">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("~~")) {
      nodes.push(<del key={key}>{renderInline(token.slice(2, -2), key)}</del>);
    } else if (/^https?:\/\//i.test(token)) {
      nodes.push(
        <a key={key} href={token} className="text-primary underline underline-offset-4 break-all" target="_blank" rel="noopener noreferrer">
          {token}
        </a>,
      );
    } else if (token.startsWith("*")) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    } else {
      nodes.push(
        <code key={key} className="rounded bg-muted px-1.5 py-0.5 text-[0.9em]">
          {token.slice(1, -1)}
        </code>,
      );
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const lines = (source ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/<a\s+(?:id|name)\s*=\s*["'][^"']*["']\s*>\s*<\/a>/gi, "")
    .split("\n");
  const at = (n: number): string => lines[n] ?? "";
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = at(i);

    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (line.startsWith("```")) {
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !at(i).startsWith("```")) {
        code.push(at(i));
        i += 1;
      }
      i += 1;
      blocks.push(
        <pre
          key={key++}
          className="my-6 overflow-x-auto rounded-xl border border-border bg-card p-4 text-sm"
        >
          <code>{code.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    if (isTableStart(lines, i)) {
      const head = splitRow(line);
      const aligns = splitRow(at(i + 1)).map((c) =>
        c.startsWith(":") && c.endsWith(":") ? "center" : c.endsWith(":") ? "end" : c.startsWith(":") ? "start" : undefined,
      );
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && at(i).trim() && at(i).includes("|")) {
        rows.push(splitRow(at(i)));
        i += 1;
      }
      const cols = head.length;
      const tk = key;
      const alignCls = (c: number) =>
        aligns[c] === "center" ? "text-center" : aligns[c] === "end" ? "text-end" : "text-start";
      blocks.push(
        <div key={key++} className="md-table-wrap my-8 overflow-x-auto rounded-xl border border-border">
          <table className="md-table w-full border-collapse text-sm md:text-base tabular-nums">
            <thead className="bg-muted">
              <tr>
                {head.map((c, ci) => (
                  <th key={ci} scope="col" className={`border-b border-border px-4 py-3 align-bottom font-semibold text-foreground whitespace-nowrap ${alignCls(ci)}`}>
                    {renderInline(c, `t${tk}h${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="even:bg-muted/40">
                  {Array.from({ length: cols }, (_, ci) => (
                    <td key={ci} className={`border-t border-border px-4 py-3 align-top text-muted-foreground ${alignCls(ci)}`}>
                      {renderInline(r[ci] ?? "", `t${tk}r${ri}c${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = (heading[1] ?? "").length;
      const content = renderInline(heading[2] ?? "", `h${key}`);
      const sizes = [
        "mt-10 text-3xl font-bold",
        "mt-10 text-2xl font-bold",
        "mt-8 text-xl font-semibold",
        "mt-6 text-lg font-semibold",
      ];
      const cls = `font-display text-balance-tight text-foreground ${sizes[level - 1] ?? sizes[0]}`;
      blocks.push(
        level === 1 ? (
          <h2 key={key++} className={cls}>
            {content}
          </h2>
        ) : level === 2 ? (
          <h2 key={key++} className={cls}>
            {content}
          </h2>
        ) : level === 3 ? (
          <h3 key={key++} className={cls}>
            {content}
          </h3>
        ) : (
          <h4 key={key++} className={cls}>
            {content}
          </h4>
        ),
      );
      i += 1;
      continue;
    }

    if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      blocks.push(<hr key={key++} className="my-10 border-border" />);
      i += 1;
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(at(i))) {
        quote.push(at(i).replace(/^>\s?/, ""));
        i += 1;
      }
      blocks.push(
        <blockquote
          key={key++}
          className="my-6 border-s-2 border-primary ps-5 text-lg text-foreground/90 italic"
        >
          {renderInline(quote.join(" "), `q${key}`)}
        </blockquote>,
      );
      continue;
    }

    const ordered = /^\d+[.)]\s+/.test(line);
    if (ordered || /^([-*+]|[•‣◦·])\s+/.test(line)) {
      const items: string[] = [];
      const test = ordered ? /^\d+[.)]\s+/ : /^([-*+]|[•‣◦·])\s+/;
      while (i < lines.length && test.test(at(i).trimStart())) {
        items.push(at(i).trimStart().replace(test, ""));
        i += 1;
      }

      const listItems = items.map((item, index) => (
        <li key={index} className="ps-1">
          {renderInline(item, `li${key}-${index}`)}
        </li>
      ));
      blocks.push(
        ordered ? (
          <ol key={key++} className="my-5 list-decimal space-y-2 ps-6 text-muted-foreground">
            {listItems}
          </ol>
        ) : (
          <ul key={key++} className="my-5 list-disc space-y-2 ps-6 text-muted-foreground">
            {listItems}
          </ul>
        ),
      );
      continue;
    }

    const paragraph: string[] = [];
    while (
      i < lines.length &&
      at(i).trim() &&
      !/^(#{1,4}\s|>|```|([-*+]|[•‣◦·])\s|\d+[.)]\s)/.test(at(i).trimStart()) &&
      !/^(-{3,}|\*{3,})$/.test(at(i).trim()) &&
      !(paragraph.length > 0 && isTableStart(lines, i))
    ) {
      paragraph.push(at(i));
      i += 1;
    }
    blocks.push(
      <p key={key++} className="my-5 leading-relaxed text-muted-foreground">
        {renderInline(paragraph.join(" "), `p${key}`)}
      </p>,
    );
  }

  return (
    <div className={className}>
      {blocks.map((block, index) => (
        <Fragment key={index}>{block}</Fragment>
      ))}
    </div>
  );
}
